import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../api/client";

const AuthContext = createContext(null);

function readSavedUser() {
  try {
    const saved = localStorage.getItem("tazy_user");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

function hasAccessToken() {
  return Boolean(localStorage.getItem("tazy_access_token"));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readSavedUser);
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [tokenVersion, setTokenVersion] = useState(() => localStorage.getItem("tazy_access_token") || "");

  const isAuthenticated = Boolean(tokenVersion || hasAccessToken());

  async function refreshUser() {
    const access = localStorage.getItem("tazy_access_token");
    if (!access) {
      setUser(null);
      setTokenVersion("");
      return null;
    }
    const res = await authApi.me();
    localStorage.setItem("tazy_user", JSON.stringify(res.data));
    setUser(res.data);
    setTokenVersion(localStorage.getItem("tazy_access_token") || access);
    return res.data;
  }

  useEffect(() => {
    if (!hasAccessToken()) {
      setBooting(false);
      return;
    }

    refreshUser()
      .catch(() => {
        localStorage.removeItem("tazy_access_token");
        localStorage.removeItem("tazy_refresh_token");
        localStorage.removeItem("tazy_user");
        setUser(null);
        setTokenVersion("");
      })
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => {
    function syncFromStorage(event) {
      if (["tazy_access_token", "tazy_refresh_token", "tazy_user"].includes(event.key)) {
        setUser(readSavedUser());
        setTokenVersion(localStorage.getItem("tazy_access_token") || "");
      }
    }
    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, []);

  async function login(email, password) {
    setLoading(true);
    try {
      const res = await authApi.login({ email, password });
      localStorage.setItem("tazy_access_token", res.data.access);
      localStorage.setItem("tazy_refresh_token", res.data.refresh);
      localStorage.setItem("tazy_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      setTokenVersion(res.data.access);
      return res.data.user;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    localStorage.removeItem("tazy_access_token");
    localStorage.removeItem("tazy_refresh_token");
    localStorage.removeItem("tazy_user");
    setUser(null);
    setTokenVersion("");
  }

  const value = useMemo(
    () => ({ user, loading, booting, isAuthenticated, login, logout, refreshUser }),
    [user, loading, booting, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
